import { Feature, FeatureCollection } from "shared/types/MapShapes";
import { getUniqueId } from "../../core/utils";

/**
 * Convierte un GeoJSON de tipo FeatureCollection en datos tabulares.
 * @param {Object} geojson - Un objeto GeoJSON de tipo FeatureCollection.
 * @returns {Object} - Un objeto con `headers` y `rows` para una tabla.
 */
export function generateTableFromGeoJSON(geojson) {
  if (!geojson || geojson.type !== "FeatureCollection" || !geojson.features) {
    throw new Error("El archivo no es un GeoJSON válido o no tiene características.");
  }

  // Crear las filas de la tabla a partir de las características (sin incluir coordenadas)
  const rows = geojson.features.map((feature) => {
    const { properties, feature_unique_id } = feature;

    // Procesar cada propiedad para asegurarnos de que los valores sean legibles
    const processedProperties = {};
    Object.entries(properties).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        processedProperties[key] = ""; // Representar valores nulos/vacíos como cadena vacía
      } else if (typeof value === "object") {
        processedProperties[key] = JSON.stringify(value); // Convertir objetos/arrays a JSON
      } else {
        processedProperties[key] = value; // Mantener valores primitivos
      }
    });

    return { feature_unique_id: getUniqueId(), ...processedProperties };
  });

  // Crear las cabeceras dinámicamente a partir de las claves de las propiedades
  const allKeys = new Set();
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => allKeys.add(key));
  });

  const headers = Array.from(allKeys).map((key: string) => ({
    text: key.charAt(0).toUpperCase() + key.slice(1),
    value: key,
  })).filter((header) => header.value !== "feature_unique_id");

  return { headers, rows };
}


/**
 * Agrega un ID unico a cada feature en un GeoJSON de tipo FeatureCollection.
 * @param {Object} geojson - Un objeto GeoJSON de tipo FeatureCollection.
 * @returns {Object} - geojson - Un objeto GeoJSON con un ID unico para cada feature
 */
export const addUniqueIdToGeojsonFeatures = (geoJsonFeatureCollection) => {
  if (!geoJsonFeatureCollection || geoJsonFeatureCollection.type !== 'FeatureCollection') {
    console.error('El objeto proporcionado no es un FeatureCollection válido');
    return null;
  }

  geoJsonFeatureCollection.features = geoJsonFeatureCollection.features.map((feature, index) => {
    if (!feature.properties) {
      feature.properties = {};
    }
    feature.properties.feature_unique_id = getUniqueId();
    return feature;
  });

  return geoJsonFeatureCollection;
}

/**
 * Lee un FeatureCollection y filtra los features con una lista de IDs.
 * @param {Object} geojson - Un objeto GeoJSON de tipo FeatureCollection.
 * @param {Object} ids - La lista de IDs a filtrar.
 * @param {Object} idKey - La clave de la propiedad que contiene el ID.
 * @returns {Object} - Un featureCollection con los features filtrados.
 */
export const filterFeaturesById = (geojson, ids, idKey = 'feature_unique_id') => {
  const filteredFeatures = geojson.features.filter((feature) => {
    let featureId = feature['properties'][idKey];
    return featureId && ids.includes(featureId);
  });

  return {
    type: 'FeatureCollection',
    features: filteredFeatures,
  }
}

/**
 * @param {Array} coordinates - Un array de objetos con latitud y longitud.
 * @returns {Object} - Un objeto GeoJSON con las coordenadas proporcionadas.
 */
export const coordinatesToGeoJSON = (coordinates) => {
  // Reordena correctamente a [lng, lat]
  const reorderedCoordinates = coordinates.map(point => [
    parseFloat(point.lng),
    parseFloat(point.lat)
  ]);

  // Cierra el polígono si no está cerrado
  if (reorderedCoordinates.length > 2) {
    const firstPoint = reorderedCoordinates[0];
    const lastPoint = reorderedCoordinates[reorderedCoordinates.length - 1];
    if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
      reorderedCoordinates.push([...firstPoint]); // ✅ cerrar correctamente con [lng, lat]
    }
  }

  const geojson: FeatureCollection = {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "MultiPolygon",
          coordinates: [
            [reorderedCoordinates]
          ]
        },
        properties: {
          customId: "d6e61975-9bce-4080-bec2-d742e7714329"
        }
      }
    ]
  };

  return geojson;
};



const POLYGON_GEOMETRY_TYPES = ['Polygon', 'MultiPolygon'];

/**
 * Filtra un FeatureCollection dejando solo geometrías Polygon / MultiPolygon.
 */
export const filterPolygonFeatures = (geojson: FeatureCollection | null): FeatureCollection => {
  if (!geojson || geojson.type !== 'FeatureCollection' || !Array.isArray(geojson.features)) {
    return { type: 'FeatureCollection', features: [] };
  }

  return {
    type: 'FeatureCollection',
    features: geojson.features.filter(
      (feature) => feature && feature.geometry && POLYGON_GEOMETRY_TYPES.includes(feature.geometry.type)
    ),
  };
};

/**
 * Concatena features de dos FeatureCollections (suma al mapa general).
 */
export const mergeFeatureCollections = (
  existing: FeatureCollection | null,
  incoming: FeatureCollection | null
): FeatureCollection => {
  const existingFeatures = existing && Array.isArray(existing.features) ? existing.features : [];
  const incomingFeatures = incoming && Array.isArray(incoming.features) ? incoming.features : [];

  return {
    type: 'FeatureCollection',
    features: [...existingFeatures, ...incomingFeatures],
  };
};

/**
 * @param features - Un array de objetos GeoJSON de tipo Feature
 * @returns - Un unico Feature con las geometrías combinadas
 */
export const combineGeojsonFeaturesIntoOne = (features) => {
  // Inicializar un array para las coordenadas combinadas
  const combinedCoordinates:any = [];

  // Iterar sobre las features
  features.forEach((feature) => {
      const geometry = feature.geometry;

      // Si es un MultiPolygon, añadir todas sus coordenadas
      if (geometry.type === "MultiPolygon") {
          combinedCoordinates.push(...geometry.coordinates);
      }
      // Si es un Polygon, convertirlo a MultiPolygon y añadir sus coordenadas
      else if (geometry.type === "Polygon") {
          combinedCoordinates.push([geometry.coordinates]);
      }
  });

  // Crear una única Feature con tipo MultiPolygon
  const combinedFeature = {
      type: "Feature",
      properties: {}, // Puedes personalizar las propiedades combinadas si es necesario
      geometry: {
          type: "MultiPolygon",
          coordinates: combinedCoordinates,
      },
  };

  return combinedFeature;
};